/**
 * DCMS DateRange Calendar — interactive two-month range picker.
 *
 * Opt-in replacement for the native-input date range UI of the DateTimePicker
 * component. Activated per instance by `helperSetCalendar(true)` (PHP), which
 * renders a `[data-dcms-calendar]` element carrying the JSON configuration.
 * Without that flag nothing here runs and the legacy markup keeps working.
 *
 * Auto-initialised on DOMContentLoaded and for nodes injected later (AJAX,
 * formBuilder, deferred render) via MutationObserver.
 *
 * Public API per instance (also exposed as globals for parity with the legacy
 * compact template, which Recordset list/search templates depend on):
 *   window['{id}_validate']()          — returns error message or ''
 *   window['{id}_generatePillData']()  — filter-pill payload or null
 *   window['{id}_resetFilterValue']()  — clears both bounds
 *   window['{id}_setMode']('single'|'range')
 *   window['setRange_{name}'](min, max)
 *
 * Month and weekday names come from `Intl` using the CMS locale passed in the
 * config — unlike the native input, which follows the operating system locale.
 *
 * Values are naive wall-clock times (the column is a timestamp without a time
 * zone), held as Dates in UTC fields — always built with Date.UTC and read with
 * getUTC*. Local Dates would shift an hour that does not exist in the browser's
 * zone (2026-03-29 02:30 in Europe/Warsaw reads back as 03:30). The current
 * clock enters through today() only.
 *
 * All styling lives in main.css (.dcms-drc-*) because the overlay is moved to
 * the TOP document when the picker runs inside an iframe, where template-local
 * styles would not apply.
 *
 * @author AMPD Sp. z o.o.
 * @version 1.0
 */

/** Value format used on the hidden inputs — must match DateTimePicker::render(). */
const WIRE_DATE = 'Y-m-d';
const WIRE_DATETIME = 'Y-m-d\\TH:i';

/** Fallback labels; real ones arrive translated in the config `i18n` bundle. */
const FALLBACK_I18N = {
    apply: 'Apply',
    cancel: 'Cancel',
    reset: 'Reset',
    from: 'From',
    to: 'To',
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisYear: 'This year',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    clear: 'Clear',
};

/**
 * Built-in presets. Ranges are computed on click (never at build time) so
 * "today" always refers to the current clock, and the boundaries mirror
 * DateTimePicker::getRangeOptions() in PHP — whole calendar days.
 */
const PRESETS = [
    { key: 'today', range: () => [startOfDay(today()), endOfDay(today())] },
    {
        key: 'yesterday',
        range: () => {
            const d = addDays(today(), -1);
            return [startOfDay(d), endOfDay(d)];
        },
    },
    { key: 'last7Days', range: () => [startOfDay(addDays(today(), -6)), endOfDay(today())] },
    { key: 'last30Days', range: () => [startOfDay(addDays(today(), -29)), endOfDay(today())] },
    {
        key: 'thisMonth',
        range: () => {
            const n = today();
            return [
                naiveDate(n.getUTCFullYear(), n.getUTCMonth(), 1),
                endOfDay(naiveDate(n.getUTCFullYear(), n.getUTCMonth() + 1, 0)),
            ];
        },
    },
    {
        key: 'lastMonth',
        range: () => {
            const n = today();
            return [
                naiveDate(n.getUTCFullYear(), n.getUTCMonth() - 1, 1),
                endOfDay(naiveDate(n.getUTCFullYear(), n.getUTCMonth(), 0)),
            ];
        },
    },
    {
        key: 'thisYear',
        range: () => {
            const y = today().getUTCFullYear();
            return [naiveDate(y, 0, 1), endOfDay(naiveDate(y, 11, 31))];
        },
    },
];

// ─── date helpers ────────────────────────────────────────────────────────────

/**
 * Naive wall-clock value (see the file header); months and days overflow like
 * the Date constructor (day 0 is the last day of the previous month).
 */
function naiveDate(y, m, d, h = 0, i = 0, s = 0) {
    return new Date(Date.UTC(y, m, d, h, i, s, 0));
}

/** The browser's current wall-clock time as a naive value. */
function today() {
    const n = new Date();
    return naiveDate(n.getFullYear(), n.getMonth(), n.getDate(), n.getHours(), n.getMinutes(), n.getSeconds());
}

function startOfDay(d) {
    const c = new Date(d);
    c.setUTCHours(0, 0, 0, 0);
    return c;
}

function endOfDay(d) {
    const c = new Date(d);
    c.setUTCHours(23, 59, 59, 0);
    return c;
}

function addDays(d, n) {
    const c = new Date(d);
    c.setUTCDate(c.getUTCDate() + n);
    return c;
}

function sameDay(a, b) {
    return !!a && !!b
        && a.getUTCFullYear() === b.getUTCFullYear()
        && a.getUTCMonth() === b.getUTCMonth()
        && a.getUTCDate() === b.getUTCDate();
}

/** Midnight-normalised comparison, so time-of-day never affects range logic. */
function dayValue(d) {
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function pad(n) {
    return n < 10 ? '0' + n : String(n);
}

/**
 * Serialises a Date to the wire format expected by the PHP component.
 *
 * @param {Date|null} d Date to serialise.
 * @param {boolean} withTime Whether the field carries a time part.
 * @return {string} `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm`; empty string for null.
 */
function toWire(d, withTime) {
    if (!d) return '';
    const date = d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
    return withTime ? date + 'T' + pad(d.getUTCHours()) + ':' + pad(d.getUTCMinutes()) : date;
}

/**
 * Parses a wire-format value back to a Date.
 *
 * @param {string} value Value from a hidden input.
 * @return {Date|null} Parsed date, or null when empty/unparsable.
 */
function fromWire(value) {
    if (!value) return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (!m) return null;
    return naiveDate(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0);
}

/**
 * Parses the upper bound: a bound without a time part covers its whole day,
 * so a field with a time accepts any hour of that day.
 *
 * @param {string} value `max` of the config.
 * @return {Date|null}
 */
function maxFromWire(value) {
    const d = fromWire(value);
    return d && !/[T ]\d{2}:\d{2}/.test(value) ? endOfDay(d) : d;
}

// ─── text input format (helperSetTextInput) ──────────────────────────────────

/**
 * Widths of the pattern tokens — mirror of DateTimeInputFormat (PHP). Order
 * matters: `HH24` must be tried before `MM` / `MI`.
 */
const INPUT_TOKENS = [['YYYY', 4], ['HH24', 2], ['MM', 2], ['MI', 2], ['DD', 2]];
const INPUT_SEPARATORS = '-./: ';

/**
 * Splits a pattern such as `YYYY-MM-DD HH24:MI` into tokens and separators.
 *
 * @param {string} pattern Pattern checked by DateTimeInputFormat::normalize() (PHP).
 * @return {Array<{token?: string, width?: number, sep?: string}>|null} Parts, or null for an unsupported pattern.
 */
export function compileInputFormat(pattern) {
    const parts = [];
    const src = String(pattern || '');
    let i = 0;
    outer: while (i < src.length) {
        for (const [token, width] of INPUT_TOKENS) {
            if (src.startsWith(token, i)) {
                parts.push({ token, width });
                i += token.length;
                continue outer;
            }
        }
        if (!INPUT_SEPARATORS.includes(src[i])) return null;
        parts.push({ sep: src[i] });
        i++;
    }
    return parts.some((p) => p.token) ? parts : null;
}

/**
 * Formats a Date with compiled pattern parts.
 *
 * @param {Date|null} d Value.
 * @param {Array} parts Result of {@link compileInputFormat}.
 * @return {string} e.g. `2026-10-23 14:30`; empty string for null.
 */
export function formatInput(d, parts) {
    if (!d) return '';
    const values = {
        YYYY: String(d.getUTCFullYear()).padStart(4, '0'),
        MM: pad(d.getUTCMonth() + 1),
        DD: pad(d.getUTCDate()),
        HH24: pad(d.getUTCHours()),
        MI: pad(d.getUTCMinutes()),
    };
    return parts.map((p) => (p.token ? values[p.token] : p.sep)).join('');
}

/**
 * Applies the typing mask: keeps the digits of `raw` and puts the separators
 * of the pattern between them, so `202610231430` reads `2026-10-23 14:30`.
 * A separator is added only when a digit follows it.
 *
 * @param {string} raw Text as typed or pasted.
 * @param {Array} parts Result of {@link compileInputFormat}.
 * @return {string} Masked text.
 */
export function maskInput(raw, parts) {
    const digits = String(raw).replace(/\D/g, '');
    let out = '';
    let pos = 0;
    let pendingSep = '';
    for (const p of parts) {
        if (p.sep !== undefined) {
            pendingSep += p.sep;
            continue;
        }
        if (pos >= digits.length) break;
        const chunk = digits.slice(pos, pos + p.width);
        out += (out === '' ? '' : pendingSep) + chunk;
        pendingSep = '';
        pos += chunk.length;
        if (chunk.length < p.width) break;
    }
    return out;
}

/**
 * Parses text typed in the pattern; the whole text must match and form a
 * real date and time (2026-02-31 or 24:00 are rejected).
 *
 * @param {string} text Typed text.
 * @param {Array} parts Result of {@link compileInputFormat}.
 * @return {Date|null|false} Date, null for an empty text, false for an invalid one.
 */
export function parseInput(text, parts) {
    const src = String(text || '').trim();
    if (src === '') return null;
    let i = 0;
    const v = { YYYY: null, MM: null, DD: null, HH24: 0, MI: 0 };
    for (const p of parts) {
        if (p.sep !== undefined) {
            if (src[i] !== p.sep) return false;
            i++;
            continue;
        }
        const chunk = src.slice(i, i + p.width);
        if (!/^\d+$/.test(chunk) || chunk.length !== p.width) return false;
        v[p.token] = +chunk;
        i += p.width;
    }
    if (i !== src.length) return false;
    const d = naiveDate(v.YYYY, v.MM - 1, v.DD, v.HH24, v.MI);
    if (d.getUTCFullYear() !== v.YYYY || d.getUTCMonth() !== v.MM - 1 || d.getUTCDate() !== v.DD
        || v.HH24 > 23 || v.MI > 59 || v.YYYY < 1000) {
        return false;
    }
    return d;
}

/** Parts of the date tokens only — the footer date field of the text input mode. */
function dateOnlyParts(parts) {
    const out = [];
    for (const p of parts) {
        if (p.token === 'HH24' || p.token === 'MI') break;
        out.push(p);
    }
    while (out.length && out[out.length - 1].sep !== undefined) out.pop();
    return out;
}

/**
 * Keeps the caret behind the same number of digits after the mask rewrote
 * the text (typing or deleting in the middle of the value).
 *
 * @param {HTMLInputElement} input Field.
 * @param {Array} parts Result of {@link compileInputFormat}.
 */
function applyMask(input, parts) {
    const caret = input.selectionStart ?? input.value.length;
    const digitsBefore = input.value.slice(0, caret).replace(/\D/g, '').length;
    const masked = maskInput(input.value, parts);
    if (masked === input.value) return;
    input.value = masked;
    let pos = 0;
    let seen = 0;
    while (pos < masked.length && seen < digitsBefore) {
        if (/\d/.test(masked[pos])) seen++;
        pos++;
    }
    if (input.setSelectionRange) input.setSelectionRange(pos, pos);
}

/**
 * Backspace right after a separator deletes the digit before it — otherwise
 * the mask would put the separator straight back and the caret would not move.
 *
 * @param {KeyboardEvent} e keydown event.
 * @param {HTMLInputElement} input Field.
 */
function skipSeparatorOnBackspace(e, input) {
    if (e.key !== 'Backspace' || input.selectionStart !== input.selectionEnd) return;
    const pos = input.selectionStart;
    if (pos > 0 && /\D/.test(input.value[pos - 1])) input.setSelectionRange(pos - 1, pos - 1);
}

/**
 * First day of the week for a locale (0 = Sunday … 1 = Monday).
 * Uses Intl week info where supported, falling back to Monday — the ISO
 * default used across the CMS.
 */
function firstDayOfWeek(locale) {
    try {
        const info = new Intl.Locale(locale).weekInfo || new Intl.Locale(locale).getWeekInfo?.();
        if (info && info.firstDay) return info.firstDay === 7 ? 0 : info.firstDay;
    } catch (e) {
        /* not supported — fall through */
    }
    return 1;
}

// ─── component ───────────────────────────────────────────────────────────────

export class DCMSDateRangeCalendar {
    /**
     * @param {HTMLElement} root Element carrying `data-dcms-calendar`.
     */
    constructor(root) {
        let cfg = {};
        try {
            cfg = JSON.parse(root.getAttribute('data-dcms-calendar') || '{}');
        } catch (e) {
            cfg = {};
        }

        this.root = root;
        this.id = cfg.id || root.id;
        this.name = cfg.name || this.id;
        this.isRange = cfg.range !== false;
        this.withTime = !!cfg.time;
        this.monthsWanted = Math.max(1, Math.min(2, parseInt(cfg.months, 10) || 2));
        this.locale = cfg.locale || document.documentElement.lang || 'pl';
        this.displayFormat = ['iso', 'full', 'short'].includes(cfg.displayFormat)
            ? cfg.displayFormat
            : 'iso';
        this.splitInputs = !!cfg.splitInputs;
        this.showPresets = cfg.presets !== false && this.isRange;
        // Disabled or readonly: the calendar, the shortcuts and the clear button stay inert.
        this.locked = !!cfg.locked;
        // Text input with a typing mask — single values only (DateTimePicker::render() enforces it).
        this.inputParts = cfg.textInput && !this.isRange
            ? compileInputFormat(cfg.inputFormat || (this.withTime ? 'YYYY-MM-DD HH24:MI' : 'YYYY-MM-DD'))
            : null;
        this.textInput = !!this.inputParts;
        if (this.textInput) {
            this.footerDateParts = dateOnlyParts(this.inputParts);
            this.footerTimeParts = compileInputFormat('HH24:MI');
        }
        this.typedError = '';
        this.min = fromWire(cfg.min);
        this.max = maxFromWire(cfg.max);
        this.i18n = Object.assign({}, FALLBACK_I18N, cfg.i18n || {});
        this.mode = this.isRange ? 'range' : 'single';

        this.hiddenStart = document.getElementById(this.id);
        this.hiddenEnd = document.getElementById(this.id + '_1');
        this.display = document.getElementById(this.id + '_display');
        this.displayEnd = document.getElementById(this.id + '_display_end');
        this.clearBtn = document.getElementById(this.id + '_clear');
        this.openBtn = document.getElementById(this.id + '_open');
        // Parsley proxy of a required field — see syncValidationInput().
        this.validationInput = document.getElementById(this.id + '_validation');

        // Committed state (mirrors the hidden inputs).
        this.start = fromWire(this.hiddenStart ? this.hiddenStart.value : '');
        this.end = fromWire(this.hiddenEnd ? this.hiddenEnd.value : '');
        // Draft state — only copied onto the hidden inputs on Apply.
        this.draftStart = null;
        this.draftEnd = null;
        this.hoverDate = null;
        this.activeSide = 'start';
        this.viewMonth = null;
        this.overlay = null;
        this.lastFocused = null;

        this.inlinePresets = !!cfg.inlinePresets;

        this.bind();
        this.buildInlinePresets();
        this.syncDisplay();
        this.syncValidationInput(false);
        this.exposeGlobals();
        this.applyInitialValidation(cfg.validationState, cfg.validationMessage);
    }

    /**
     * Renders the range shortcuts next to the field, so a common range can be
     * picked without opening the calendar at all. Mirrors the preset row of the
     * legacy `dateTimeRangePicker.tpl`.
     */
    buildInlinePresets() {
        const host = this.root.querySelector('[data-dcms-calendar-presets]');
        if (!host || !this.inlinePresets || !this.isRange) return;

        // A disabled or readonly field must not be editable through the shortcuts
        // either — they write straight to the value, bypassing the overlay. (The
        // display input's own `readonly` is no criterion: it is readonly by design.)
        const locked = this.locked || !!(this.display && this.display.disabled);

        PRESETS.forEach((p) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'size-1 btn bg-soft-indigo';
            b.disabled = locked;
            b.textContent = this.i18n[p.key] || p.key;
            b.addEventListener('click', (e) => {
                e.preventDefault();
                const [from, to] = p.range();
                this.start = this.withTime ? from : startOfDay(from);
                this.end = this.withTime ? to : startOfDay(to);
                this.commit();
                this.markInlineActive();
            });
            host.appendChild(b);
        });

        const reset = document.createElement('button');
        reset.type = 'button';
        reset.className = 'size-1 btn bg-slate';
        reset.id = 'reset_' + this.name;
        reset.disabled = locked;
        reset.textContent = this.i18n.reset;
        reset.addEventListener('click', (e) => {
            e.preventDefault();
            this.clear();
            this.markInlineActive();
        });
        host.appendChild(reset);

        this.inlinePresetsHost = host;
        this.markInlineActive();
    }

    /** Highlights the inline shortcut matching the committed value. */
    markInlineActive() {
        if (!this.inlinePresetsHost) return;
        const s = this.start ? dayValue(this.start) : null;
        const e = this.end ? dayValue(this.end) : null;
        [...this.inlinePresetsHost.children].forEach((btn, i) => {
            const preset = PRESETS[i];
            if (!preset) return;
            const [from, to] = preset.range();
            btn.classList.toggle(
                'is-active',
                s !== null && e !== null && s === dayValue(from) && e === dayValue(to)
            );
        });
    }

    // ── wiring ───────────────────────────────────────────────────────────────

    bind() {
        if (this.textInput) {
            this.bindTextInput();
            return;
        }
        const openers = [this.display, this.displayEnd].filter(Boolean);
        openers.forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                this.open(el === this.displayEnd ? 'end' : 'start');
            });
            el.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.open(el === this.displayEnd ? 'end' : 'start');
                }
            });
        });

        if (this.clearBtn) {
            this.clearBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (this.locked) return;
                this.clear();
            });
        }
    }

    /**
     * Wires the text input mode: masking while typing, commit on change / Enter,
     * Alt+↓ and the calendar button open the picker, and a form with an
     * unparsable value is not submitted (the hidden input would still hold the
     * previous value).
     */
    bindTextInput() {
        const input = this.display;
        if (!input) return;

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                // Enter commits the typed value instead of submitting the form.
                e.preventDefault();
                this.commitTyped();
            } else if (e.key === 'ArrowDown' && e.altKey) {
                e.preventDefault();
                this.openPicker();
            } else {
                skipSeparatorOnBackspace(e, input);
            }
        });
        input.addEventListener('input', () => applyMask(input, this.inputParts));
        input.addEventListener('change', () => this.commitTyped());

        // A value written into the hidden input from outside — Editable's Cancel
        // puts the committed value back there — is read again: the text shows it
        // in the pattern and the error of the typed text goes away.
        if (this.hiddenStart) {
            this.hiddenStart.addEventListener('change', () => {
                if (this.committing) return;
                this.setTypedError('');
                this.start = fromWire(this.hiddenStart.value);
                this.syncDisplay();
                this.syncValidationInput(true);
            });
        }

        if (this.openBtn) {
            this.openBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.openPicker();
            });
        }

        const form = this.root.closest ? this.root.closest('form') : null;
        if (form) {
            form.addEventListener('submit', (e) => {
                // A value typed right before Enter in another field has had no change event yet.
                if (document.activeElement === input) this.commitTyped();
                if (!this.typedError) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                input.focus();
            }, true);
        }
    }

    /**
     * Commits the text of the text input: empty clears the value, a valid date
     * becomes the value (and is reprinted in the pattern), anything else shows
     * the format hint under the field and leaves the value untouched.
     */
    commitTyped() {
        if (this.locked) return;
        const text = this.display.value;
        const parsed = parseInput(text, this.inputParts);
        if (parsed === false) {
            this.setTypedError(this.i18n.invalidDate || '');
            return;
        }
        if (parsed && this.isOutOfRange(parsed)) {
            this.setTypedError(this.i18n.outOfRange || '');
            return;
        }
        this.setTypedError('');
        const same = (parsed === null && this.start === null)
            || (parsed && this.start && parsed.getTime() === this.start.getTime());
        this.start = parsed;
        if (same) this.syncDisplay();
        else this.commit();
    }

    /**
     * Shows (non-empty message) or clears the error of the typed text.
     *
     * @param {string} message Error message; empty clears it.
     */
    setTypedError(message) {
        const had = this.typedError !== '';
        this.typedError = message;
        // aria-invalid: read out with the field, and Editable (a list cell)
        // does not save while it is set — the hidden input holds the old value.
        if (this.display) {
            if (message) this.display.setAttribute('aria-invalid', 'true');
            else this.display.removeAttribute('aria-invalid');
        }
        if (typeof window.setValidationState !== 'function') return;
        if (message) window.setValidationState(this.id, 2, message);
        else if (had) window.setValidationState(this.id, 0, '');
    }

    /**
     * Opens the picker of the text input mode: the system date picker on touch
     * devices (when the browser can open one), the DCMS calendar otherwise.
     */
    openPicker() {
        if (this.locked) return;
        const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (coarse && typeof HTMLInputElement !== 'undefined'
            && typeof HTMLInputElement.prototype.showPicker === 'function') {
            const native = this.nativePicker();
            native.value = toWire(this.start, this.withTime);
            try {
                native.showPicker();
                return;
            } catch (e) {
                // No user activation or an unsupported type — fall back to the calendar.
            }
        }
        this.open('start');
    }

    /**
     * Invisible native input that hosts the system picker on touch devices;
     * its value goes back to the field in the pattern of the text input.
     *
     * @return {HTMLInputElement}
     */
    nativePicker() {
        if (this.nativeInput) return this.nativeInput;
        const native = document.createElement('input');
        native.type = this.withTime ? 'datetime-local' : 'date';
        native.className = 'dcms-drc-native sr-only';
        native.tabIndex = -1;
        native.setAttribute('aria-hidden', 'true');
        // Not a form field of its own: Parsley would turn min/max into a
        // number range and reject every datetime-local value.
        native.setAttribute('data-parsley-excluded', 'true');
        if (this.min) native.min = toWire(this.min, this.withTime);
        if (this.max) native.max = toWire(this.max, this.withTime);
        native.addEventListener('change', () => {
            const picked = fromWire(native.value);
            if (!picked) return;
            this.setTypedError('');
            this.start = picked;
            this.commit();
        });
        (this.display.parentNode || this.root).appendChild(native);
        this.nativeInput = native;
        return native;
    }

    /**
     * Publishes the legacy global helpers. Recordset list/search templates call
     * these by name, so the calendar mode must provide them too.
     */
    exposeGlobals() {
        const w = window;
        w[this.id + '_validate'] = () => this.validate();
        w[this.id + '_generatePillData'] = () => this.pillData();
        w[this.id + '_resetFilterValue'] = () => this.clear();
        w[this.id + '_setMode'] = (mode) => this.setMode(mode);
        w['setRange_' + this.name] = (min, max) => {
            this.start = fromWire(min);
            this.end = fromWire(max);
            this.commit();
        };
    }

    /**
     * Shows the validation state the server gave the field (helperSetValidation(),
     * the error of a rejected save). The native-input templates call
     * setValidationState() from an inline script; this template has none, and the
     * module also starts fields injected after the page load (AJAX, formBuilder),
     * which an inline DOMContentLoaded call would miss.
     *
     * setValidationState() (main.js) marks the hidden value inputs, fills
     * `{id}-helpText` and marks the tab; main.css frames the display inputs from
     * the class of the hidden field. State 0 needs no call — a new field has no
     * state to clear.
     *
     * @param {number|undefined} state ValidationResult value: 0 none, 1 success, 2 error.
     * @param {string|undefined} message Message shown under the field.
     */
    applyInitialValidation(state, message) {
        const value = parseInt(state, 10) || 0;
        if (!value || typeof window.setValidationState !== 'function') return;
        window.setValidationState(this.id, value, message || '');
    }

    // ── state ────────────────────────────────────────────────────────────────

    /** @param {'single'|'range'} mode */
    setMode(mode) {
        this.mode = mode === 'single' ? 'single' : 'range';
        if (this.mode === 'single') {
            this.end = null;
            this.commit();
        }
    }

    /** Writes committed state to the hidden inputs and refreshes the display. */
    commit() {
        // A value picked in the calendar replaces text that could not be parsed.
        if (this.textInput) this.setTypedError('');
        if (this.hiddenStart) this.hiddenStart.value = toWire(this.start, this.withTime);
        if (this.hiddenEnd) this.hiddenEnd.value = toWire(this.end, this.withTime);
        this.syncDisplay();
        this.markInlineActive();
        this.syncValidationInput(true);
        // Own change events — the text input mode does not read them back.
        this.committing = true;
        try {
            [this.hiddenStart, this.hiddenEnd].forEach((el) => {
                if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
            });
        } finally {
            this.committing = false;
        }
    }

    /**
     * Keeps the Parsley proxy of a required field in step with the value.
     *
     * Parsley never validates the hidden inputs (the form's `excluded` holds
     * input[type=hidden]), so for `required` the template renders a visually
     * hidden text input `{id}_validation` with `data-parsley-required`. It
     * holds the value while the field is complete — the whole range, or the
     * date in single mode — and is empty otherwise, so an empty or half-open
     * range fails the required check.
     *
     * After a change the proxy gets an `input` event, as if the value had
     * been typed, and Parsley treats it like any text field: before the first
     * failed validation nothing is shown; after it the field is validated
     * again at once, so the message goes away when a value is picked and comes
     * back when the field is cleared. The event does not bubble — the form
     * already hears the change from the hidden inputs.
     *
     * @param {boolean} notify Whether to signal the change to Parsley (false on init).
     */
    syncValidationInput(notify) {
        const input = this.validationInput;
        if (!input) return;
        const complete = this.isRange && this.mode === 'range'
            ? !!(this.start && this.end)
            : !!this.start;
        input.value = complete ? this.formatValueFull() : '';
        if (notify) input.dispatchEvent(new Event('input'));
    }

    clear() {
        this.start = null;
        this.end = null;
        this.commit();
    }

    validate() {
        const required = this.root.getAttribute('data-required') === '1';
        if (this.isRange && this.mode === 'range') {
            if (!this.start && !this.end) return required ? (this.i18n.requiredMessage || '') : '';
            if (!this.start || !this.end) return this.i18n.bothRequiredMessage || '';
            if (dayValue(this.start) > dayValue(this.end)) return this.i18n.rangeError || '';
            return '';
        }
        return !this.start && required ? (this.i18n.requiredMessage || '') : '';
    }

    pillData() {
        if (!this.start && !this.end) return null;
        const labelEl = this.fieldLabel();
        const label = labelEl ? labelEl.textContent.trim().replace(/[*:]/g, '') : this.name;
        return { label: label, displayValue: this.formatValue(), fieldName: this.name + '[]' };
    }

    /**
     * Finds the field's own label for the filter pill.
     *
     * The template points it at the display input (`{id}_display`, as in the
     * compact template); `{id}` is a hidden input. The small "from"/"to"
     * labels of the split variant point at the same id, so labels inside a
     * display wrapper are skipped. A label for `{id}` elsewhere in the form
     * is the fallback.
     *
     * @return {HTMLLabelElement|null}
     */
    fieldLabel() {
        const own = Array.from(this.root.querySelectorAll('label[for="' + this.id + '_display"]'))
            .find((el) => !el.closest('.dcms-drc-display-wrap'));
        if (own) return own;
        const form = this.root.closest('form');
        return form ? form.querySelector('label[for="' + this.id + '"]') : null;
    }

    // ── display ──────────────────────────────────────────────────────────────

    /**
     * Formats the committed value for the read-only display input, honouring
     * the locale and the `short`/`full` display format.
     *
     * @return {string} e.g. `20.08.2026 → 23.08.2026` or `czw., 20 sie — nd., 23 sie`.
     */
    /**
     * Formats one bound according to `displayFormat`.
     *
     * `iso` is the default because the rest of DCMS2 — the legacy compact
     * picker, the advanced-search rule display, the wire format itself — all
     * speak `YYYY-MM-DD`. Mixing a locale-formatted field with ISO footer
     * inputs inside one component reads as a bug.
     *
     * @param {Date|null} d Bound to format.
     * @return {string} Formatted value, or an empty string.
     */
    formatOne(d) {
        if (!d) return '';
        if (this.displayFormat === 'iso') {
            return toWire(d, this.withTime).replace('T', ' ');
        }
        const opts = this.displayFormat === 'short'
            ? { weekday: 'short', day: 'numeric', month: 'short' }
            : { day: '2-digit', month: '2-digit', year: 'numeric' };
        if (this.withTime && this.displayFormat !== 'short') {
            opts.hour = '2-digit';
            opts.minute = '2-digit';
        }
        opts.timeZone = 'UTC';
        return new Intl.DateTimeFormat(this.locale, opts).format(d);
    }

    formatValue() {
        if (!this.start && !this.end) return '';
        const a = this.formatOne(this.start);
        const b = this.formatOne(this.end);
        if (!this.isRange || this.mode === 'single') return a;
        const sep = this.displayFormat === 'short' ? ' — ' : ' → ';
        return a && b ? a + sep + b : (a || b);
    }

    /**
     * Full, unambiguous value for the title attribute of shortened displays —
     * always ISO, since that is the format the rest of the CMS shows.
     */
    formatValueFull() {
        const iso = (d) => (d ? toWire(d, this.withTime).replace('T', ' ') : '');
        const a = iso(this.start);
        const b = iso(this.end);
        return a && b ? a + ' → ' + b : (a || b);
    }

    syncDisplay() {
        const value = this.formatValue();
        if (this.textInput) {
            if (this.display) this.display.value = formatInput(this.start, this.inputParts);
        } else if (this.splitInputs && this.displayEnd) {
            // Both fields follow the same format as the single-field variant.
            if (this.display) this.display.value = this.formatOne(this.start);
            this.displayEnd.value = this.formatOne(this.end);
        } else if (this.display) {
            this.display.value = value;
            this.display.title = this.displayFormat === 'short' ? this.formatValueFull() : '';
        }
        if (this.clearBtn) {
            this.clearBtn.classList.toggle('is-hidden', !this.start && !this.end);
        }
        this.root.classList.toggle('has-value', !!(this.start || this.end));
    }

    // ── overlay lifecycle ────────────────────────────────────────────────────

    /**
     * @param {'start'|'end'} side Which bound the first click should set.
     */
    open(side) {
        if (this.overlay) return;
        if (this.locked || (this.display && this.display.disabled)) return;

        this.activeSide = this.isRange ? (side === 'end' ? 'end' : 'start') : 'start';
        // Copies: the footer time fields edit the draft, Cancel must leave the value as it was.
        this.draftStart = this.start && new Date(this.start);
        this.draftEnd = this.end && new Date(this.end);
        this.hoverDate = null;
        this.viewMonth = startOfDay(this.draftStart || today());
        this.viewMonth.setUTCDate(1);

        // Inside an iframe the overlay must live in the TOP document, otherwise
        // the iframe border clips it.
        this.doc = (window.top !== window && window.top.document) ? window.top.document : document;
        this.lastFocused = document.activeElement;

        this.overlay = this.buildOverlay();
        this.doc.body.appendChild(this.overlay);
        this.render();

        this.onKey = (e) => this.handleKey(e);
        this.doc.addEventListener('keydown', this.onKey, true);

        const firstBtn = this.overlay.querySelector('.dcms-drc-day:not(:disabled)');
        if (firstBtn) firstBtn.focus();
    }

    close() {
        if (!this.overlay) return;
        this.doc.removeEventListener('keydown', this.onKey, true);
        this.overlay.remove();
        this.overlay = null;
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    }

    /** Copies the draft onto the committed state. */
    apply() {
        this.start = this.draftStart;
        this.end = this.isRange && this.mode === 'range' ? this.draftEnd : null;
        if (this.start && this.end && dayValue(this.start) > dayValue(this.end)) {
            const t = this.start;
            this.start = this.end;
            this.end = t;
        }
        this.commit();
        this.close();
    }

    handleKey(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            return;
        }
        if (e.key === 'Enter' && e.target && e.target.classList.contains('dcms-drc-day')) {
            return; // handled by the button's own click
        }
        if (e.key === 'Tab') {
            const focusables = this.overlay.querySelectorAll(
                'button:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && this.doc.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && this.doc.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    // ── selection ────────────────────────────────────────────────────────────

    pickDate(date) {
        if (!this.isRange || this.mode === 'single') {
            this.draftStart = this.mergeTime(date, this.draftStart, false);
            this.draftEnd = null;
            this.repaintDays();
            return;
        }
        // First click (or a click before the current start) opens a new range.
        if (this.activeSide === 'start' || !this.draftStart || (this.draftStart && this.draftEnd)) {
            this.draftStart = this.mergeTime(date, this.draftStart, false);
            this.draftEnd = null;
            this.activeSide = 'end';
        } else {
            if (dayValue(date) < dayValue(this.draftStart)) {
                this.draftEnd = this.mergeTime(this.draftStart, this.draftEnd, true);
                this.draftStart = this.mergeTime(date, this.draftStart, false);
            } else {
                this.draftEnd = this.mergeTime(date, this.draftEnd, true);
            }
            this.activeSide = 'start';
        }
        this.hoverDate = null;
        this.repaintDays();
    }

    /**
     * Highlights the preset button matching the current draft, so a range set
     * from the sidebar reads back as "Last month" instead of a bare selection.
     */
    markActivePreset() {
        if (!this.presetsEl) return;
        const s = this.draftStart ? dayValue(this.draftStart) : null;
        const e = this.draftEnd ? dayValue(this.draftEnd) : null;
        [...this.presetsEl.children].forEach((btn, i) => {
            const preset = PRESETS[i];
            if (!preset) return;
            const [from, to] = preset.range();
            btn.classList.toggle(
                'is-active',
                s !== null && e !== null && s === dayValue(from) && e === dayValue(to)
            );
        });
    }

    /**
     * Keeps the time part when the user only changes the day.
     *
     * @param {Date} date Newly picked day.
     * @param {Date|null} previous Previously held value for this bound.
     * @param {boolean} isEnd Whether this is the closing bound.
     */
    mergeTime(date, previous, isEnd) {
        const out = new Date(date);
        if (!this.withTime) {
            out.setUTCHours(0, 0, 0, 0);
            return out;
        }
        if (previous) out.setUTCHours(previous.getUTCHours(), previous.getUTCMinutes(), 0, 0);
        else if (isEnd) out.setUTCHours(23, 59, 0, 0);
        else out.setUTCHours(0, 0, 0, 0);
        return out;
    }

    /** Whether a day of the grid lies outside min / max (whole days). */
    isDisabled(date) {
        if (this.min && dayValue(date) < dayValue(this.min)) return true;
        if (this.max && dayValue(date) > dayValue(this.max)) return true;
        return false;
    }

    /**
     * Whether a typed value lies outside min / max — to the minute when the
     * field carries a time, so `max` 12:00 rejects 14:30 of the same day.
     *
     * @param {Date} date Parsed value.
     * @return {boolean}
     */
    isOutOfRange(date) {
        if (!this.withTime) return this.isDisabled(date);
        return (!!this.min && date.getTime() < this.min.getTime())
            || (!!this.max && date.getTime() > this.max.getTime());
    }

    // ── DOM building ─────────────────────────────────────────────────────────

    buildOverlay() {
        const d = this.doc;
        const overlay = d.createElement('div');
        overlay.className = 'dcms-drc-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.addEventListener('mousedown', (e) => {
            if (e.target === overlay) this.close();
        });

        const panel = d.createElement('div');
        panel.className = 'dcms-drc-panel';
        if (this.doc !== document && document.documentElement.classList.contains('dark')) {
            panel.classList.add('dcms-drc-force-dark');
        }
        overlay.appendChild(panel);

        const body = d.createElement('div');
        body.className = 'dcms-drc-body';
        panel.appendChild(body);

        if (this.showPresets) {
            this.presetsEl = d.createElement('div');
            this.presetsEl.className = 'dcms-drc-presets';
            PRESETS.forEach((p) => {
                const b = d.createElement('button');
                b.type = 'button';
                b.className = 'dcms-drc-preset';
                b.textContent = this.i18n[p.key] || p.key;
                b.addEventListener('click', () => {
                    const [from, to] = p.range();
                    this.draftStart = this.withTime ? from : startOfDay(from);
                    this.draftEnd = this.withTime ? to : startOfDay(to);
                    this.activeSide = 'start';
                    this.viewMonth = naiveDate(from.getUTCFullYear(), from.getUTCMonth(), 1);
                    this.render();
                });
                this.presetsEl.appendChild(b);
            });
            body.appendChild(this.presetsEl);
        }

        this.monthsEl = d.createElement('div');
        this.monthsEl.className = 'dcms-drc-months';
        body.appendChild(this.monthsEl);

        const footer = d.createElement('div');
        footer.className = 'dcms-drc-footer';
        panel.appendChild(footer);

        this.fieldsEl = d.createElement('div');
        this.fieldsEl.className = 'dcms-drc-fields';
        footer.appendChild(this.fieldsEl);

        // Clears both bounds without closing the picker — the counterpart of
        // the legacy "Reset" button of dateTimeRangePicker.tpl.
        const reset = d.createElement('button');
        reset.type = 'button';
        reset.className = 'dcms-drc-reset';
        reset.id = this.id + '_modalResetBtn';
        reset.textContent = this.i18n.reset;
        reset.addEventListener('click', () => {
            this.draftStart = null;
            this.draftEnd = null;
            this.hoverDate = null;
            this.activeSide = 'start';
            this.repaintDays();
        });
        footer.appendChild(reset);

        const actions = d.createElement('div');
        actions.className = 'dcms-drc-actions';
        const cancel = d.createElement('button');
        cancel.type = 'button';
        cancel.className = 'btn size-2 bg-picker-reset';
        cancel.textContent = this.i18n.cancel;
        cancel.addEventListener('click', () => this.close());
        const apply = d.createElement('button');
        apply.type = 'button';
        apply.className = 'btn size-2 bg-picker-confirm';
        apply.id = this.id + '_confirmBtn';
        apply.textContent = this.i18n.apply;
        apply.addEventListener('click', () => {
            if (this.canApply()) this.apply();
        });
        this.applyBtn = apply;
        actions.appendChild(cancel);
        actions.appendChild(apply);
        footer.appendChild(actions);

        return overlay;
    }

    /** Rebuilds the month grids and the footer fields from the draft state. */
    render() {
        const d = this.doc;
        const months = window.matchMedia('(max-width: 640px)').matches ? 1 : this.monthsWanted;
        this.monthsEl.textContent = '';

        for (let i = 0; i < months; i++) {
            const base = naiveDate(this.viewMonth.getUTCFullYear(), this.viewMonth.getUTCMonth() + i, 1);
            this.monthsEl.appendChild(this.buildMonth(base, i === 0, i === months - 1));
        }
        this.markActivePreset();
        this.renderFields();
        this.updateApplyState();
    }

    buildMonth(base, isFirst, isLast) {
        const d = this.doc;
        const wrap = d.createElement('div');
        wrap.className = 'dcms-drc-month';

        const head = d.createElement('div');
        head.className = 'dcms-drc-month-head';

        const prev = d.createElement('button');
        prev.type = 'button';
        prev.className = 'dcms-drc-nav';
        prev.setAttribute('aria-label', this.i18n.prevMonth);
        prev.textContent = '‹';
        prev.addEventListener('click', () => {
            this.viewMonth = naiveDate(this.viewMonth.getUTCFullYear(), this.viewMonth.getUTCMonth() - 1, 1);
            this.render();
        });

        const title = d.createElement('div');
        title.className = 'dcms-drc-month-title';
        title.textContent = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
            .format(base)
            .replace(/^./, (c) => c.toUpperCase());

        const next = d.createElement('button');
        next.type = 'button';
        next.className = 'dcms-drc-nav';
        next.setAttribute('aria-label', this.i18n.nextMonth);
        next.textContent = '›';
        next.addEventListener('click', () => {
            this.viewMonth = naiveDate(this.viewMonth.getUTCFullYear(), this.viewMonth.getUTCMonth() + 1, 1);
            this.render();
        });

        // With two months side by side only the outer arrows are actionable.
        // `visibility` (not `display`) keeps both month headers aligned; the
        // class is shared with the React port.
        prev.classList.toggle('is-hidden', !isFirst);
        next.classList.toggle('is-hidden', !isLast);

        head.appendChild(prev);
        head.appendChild(title);
        head.appendChild(next);
        wrap.appendChild(head);

        const grid = d.createElement('div');
        grid.className = 'dcms-drc-grid';

        const fdw = firstDayOfWeek(this.locale);
        const dowFmt = new Intl.DateTimeFormat(this.locale, { weekday: 'short', timeZone: 'UTC' });
        for (let i = 0; i < 7; i++) {
            const dow = d.createElement('div');
            dow.className = 'dcms-drc-dow';
            // 2024-01-07 was a Sunday — a stable reference for weekday names.
            // Trailing dots ("pon.", "wt.") are dropped for a cleaner header.
            dow.textContent = dowFmt
                .format(naiveDate(2024, 0, 7 + ((fdw + i) % 7)))
                .replace(/\.$/, '');
            grid.appendChild(dow);
        }

        const firstOfMonth = naiveDate(base.getUTCFullYear(), base.getUTCMonth(), 1);
        let offset = firstOfMonth.getUTCDay() - fdw;
        if (offset < 0) offset += 7;
        const gridStart = addDays(firstOfMonth, -offset);

        for (let i = 0; i < 42; i++) {
            grid.appendChild(this.buildDay(addDays(gridStart, i), base.getUTCMonth()));
        }

        wrap.appendChild(grid);
        return wrap;
    }

    buildDay(date, currentMonth) {
        const b = this.doc.createElement('button');
        b.type = 'button';
        b.className = 'dcms-drc-day';
        b.textContent = String(date.getUTCDate());
        b.setAttribute('data-date', toWire(date, false));

        // Days spilling in from the neighbouring month are context only. They
        // never carry range state — otherwise the same day would light up twice
        // (once in its own grid, once as a spill-over in the adjacent one).
        if (date.getUTCMonth() !== currentMonth) b.classList.add('is-outside');
        if (sameDay(date, today())) b.classList.add('is-today');

        if (this.isDisabled(date)) {
            b.disabled = true;
            b.classList.add('is-disabled');
            return b;
        }

        this.applyDayState(b, date);
        b.addEventListener('click', () => this.pickDate(date));

        if (this.isRange) {
            b.addEventListener('mouseenter', () => {
                if (this.draftStart && !this.draftEnd) {
                    this.hoverDate = date;
                    // Repaint classes in place. A full render() here would
                    // replace the node under the cursor between mousedown and
                    // mouseup, so the click selecting the closing bound would
                    // never fire.
                    this.repaintDays();
                }
            });
        }
        return b;
    }

    /**
     * Applies the selection/range classes of one day button.
     *
     * @param {HTMLElement} b Day button.
     * @param {Date} date Day it represents.
     */
    applyDayState(b, date) {
        b.classList.remove('is-start', 'is-end', 'is-selected', 'is-in-range', 'is-preview');
        if (b.classList.contains('is-outside') || b.disabled) return;

        const v = dayValue(date);
        const s = this.draftStart ? dayValue(this.draftStart) : null;
        const e = this.draftEnd ? dayValue(this.draftEnd) : null;
        // While picking the closing bound, hover previews the resulting range.
        const h = this.hoverDate && s !== null && e === null ? dayValue(this.hoverDate) : null;
        const lo = h !== null ? Math.min(s, h) : s;
        const hi = h !== null ? Math.max(s, h) : e;

        if (s !== null && v === s) b.classList.add('is-start', 'is-selected');
        if (e !== null && v === e) b.classList.add('is-end', 'is-selected');
        if (lo !== null && hi !== null && v > lo && v < hi) b.classList.add('is-in-range');
        if (h !== null && (v === lo || v === hi) && v !== s) b.classList.add('is-preview');
    }

    /** Repaints day states without rebuilding the grid (hover, selection). */
    repaintDays() {
        if (!this.overlay) return;
        this.overlay.querySelectorAll('.dcms-drc-day').forEach((b) => {
            const d = fromWire(b.getAttribute('data-date'));
            if (d) this.applyDayState(b, d);
        });
        this.markActivePreset();
        this.renderFields();
        this.updateApplyState();
    }

    /**
     * A range may only be committed whole. Half-open selections are exactly
     * what `validate()` rejects, so Apply must not offer to save one.
     * Clearing both bounds stays allowed (that is how a filter is reset).
     *
     * @return {boolean} Whether the current draft may be applied.
     */
    canApply() {
        if (!this.isRange || this.mode === 'single') return true;
        const hasStart = !!this.draftStart;
        const hasEnd = !!this.draftEnd;
        return (hasStart && hasEnd) || (!hasStart && !hasEnd);
    }

    /** Reflects {@link canApply} on the Apply button. */
    updateApplyState() {
        if (!this.applyBtn) return;
        const ok = this.canApply();
        this.applyBtn.disabled = !ok;
        this.applyBtn.classList.toggle('is-disabled', !ok);
        this.applyBtn.title = ok ? '' : (this.i18n.bothRequiredMessage || '');
    }

    /** Footer inputs mirroring the draft — editable for keyboard entry. */
    renderFields() {
        const d = this.doc;
        this.fieldsEl.textContent = '';

        const makeField = (which) => {
            const wrap = d.createElement('div');
            wrap.className = 'dcms-drc-field';

            const input = d.createElement('input');
            input.type = 'text';
            input.className = 'dcms-drc-input';
            input.id = this.id + '_drc_' + which;
            input.setAttribute('aria-label', which === 'start' ? this.i18n.from : this.i18n.to);
            const value = which === 'start' ? this.draftStart : this.draftEnd;
            // The text input mode speaks its own pattern in the footer as well.
            const dateParts = this.textInput ? this.footerDateParts : null;
            input.value = dateParts ? formatInput(value, dateParts) : (value ? toWire(value, false) : '');
            if (dateParts) {
                input.inputMode = 'numeric';
                input.autocomplete = 'off';
                input.addEventListener('input', () => applyMask(input, dateParts));
                input.addEventListener('keydown', (e) => skipSeparatorOnBackspace(e, input));
            }
            input.addEventListener('change', () => {
                const parsed = dateParts ? parseInput(input.value, dateParts) : fromWire(input.value);
                if (!parsed) return;
                if (which === 'start') this.draftStart = this.mergeTime(parsed, this.draftStart, false);
                else this.draftEnd = this.mergeTime(parsed, this.draftEnd, true);
                this.viewMonth = naiveDate(parsed.getUTCFullYear(), parsed.getUTCMonth(), 1);
                this.render();
            });
            wrap.appendChild(input);

            if (this.withTime) {
                const time = d.createElement('input');
                // The native time input follows the browser locale (AM/PM in en-US);
                // the text input mode keeps 24-hour HH:MI.
                time.type = this.textInput ? 'text' : 'time';
                time.className = 'dcms-drc-time';
                time.value = value ? pad(value.getUTCHours()) + ':' + pad(value.getUTCMinutes()) : '';
                if (this.textInput) {
                    time.inputMode = 'numeric';
                    time.autocomplete = 'off';
                    time.maxLength = 5;
                    time.placeholder = '--:--';
                    time.setAttribute('aria-label', (which === 'start' ? this.i18n.from : this.i18n.to) + ' (HH:MM)');
                    time.addEventListener('input', () => applyMask(time, this.footerTimeParts));
                    time.addEventListener('keydown', (e) => skipSeparatorOnBackspace(e, time));
                }
                time.addEventListener('change', () => {
                    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(time.value)) return;
                    const parts = time.value.split(':');
                    const target = which === 'start' ? this.draftStart : this.draftEnd;
                    if (!target) return;
                    const next = new Date(target);
                    next.setUTCHours(+parts[0], +parts[1], 0, 0);
                    if (which === 'start') this.draftStart = next;
                    else this.draftEnd = next;
                    this.render();
                });
                wrap.appendChild(time);
            }
            return wrap;
        };

        this.fieldsEl.appendChild(makeField('start'));
        if (this.isRange && this.mode === 'range') {
            const arrow = d.createElement('span');
            arrow.className = 'dcms-drc-arrow';
            arrow.textContent = '→';
            this.fieldsEl.appendChild(arrow);
            this.fieldsEl.appendChild(makeField('end'));
        }
    }
}

// ─── auto-init ───────────────────────────────────────────────────────────────

const initialised = new WeakSet();

/**
 * Initialises every not-yet-initialised calendar under `root`.
 *
 * @param {ParentNode} root Subtree to scan.
 */
export function initDateRangeCalendars(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-dcms-calendar]').forEach((el) => {
        if (initialised.has(el)) return;
        initialised.add(el);
        try {
            new DCMSDateRangeCalendar(el);
        } catch (e) {
            console.error('[dcms-daterange-calendar] init failed', el, e);
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initDateRangeCalendars(document);

    // Pickers injected later (AJAX dataset render, formBuilder, deferred render).
    const mo = new MutationObserver((records) => {
        records.forEach((r) => {
            r.addedNodes.forEach((n) => {
                if (n.nodeType !== 1) return;
                if (n.matches && n.matches('[data-dcms-calendar]')) initDateRangeCalendars(n.parentNode);
                else if (n.querySelector && n.querySelector('[data-dcms-calendar]')) initDateRangeCalendars(n);
            });
        });
    });
    mo.observe(document.body, { childList: true, subtree: true });
});
